import { useQuery } from '@tanstack/react-query';
import { Smartphone, Timer, UserPlus, ZapOff } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';

interface Overview {
  readonly platformMix: { readonly ios: number; readonly android: number };
  readonly activeDevices: number;
  readonly devicesNew24h: number;
  readonly coldStartP95Ms: number | null;
  readonly crashFreeSessionsPct: number | null;
}

const fmtInt = new Intl.NumberFormat('en-US');

function Kpi({
  label,
  display,
  Icon,
  detail,
}: {
  readonly label: string;
  readonly display: string;
  readonly Icon: typeof Smartphone;
  readonly detail?: string;
}) {
  return (
    <div className="nw-kpi">
      <div className="nw-kpi__head">
        <span className="nw-kpi__icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="nw-kpi__label">{label}</span>
      </div>
      <div className={display === '—' ? 'nw-kpi__value nw-kpi__value--null' : 'nw-kpi__value'}>
        {display}
      </div>
      {detail && <span className="nw-kpi__delta-note">{detail}</span>}
    </div>
  );
}

export function Mobile() {
  const query = useQuery({
    queryKey: queryKeys.mobile.overview(),
    queryFn: async () => {
      const res = await apiClient.get<Overview>('/owner/mobile/overview');
      return res.data;
    },
  });

  const data = query.data;
  const platformTotal = (data?.platformMix.ios ?? 0) + (data?.platformMix.android ?? 0);
  const iosPct =
    platformTotal === 0 ? 0 : ((data?.platformMix.ios ?? 0) / platformTotal) * 100;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Mobile</h1>
        <p className="nw-page-header__subtitle">
          Platform mix, device counts, cold-start and crash-free numbers.
        </p>
      </header>

      <div className="nw-kpi-grid">
        <Kpi
          label="Active devices"
          display={data ? fmtInt.format(data.activeDevices) : '—'}
          Icon={Smartphone}
        />
        <Kpi
          label="Platform mix"
          display={
            data
              ? `${iosPct.toFixed(0)}% iOS / ${(100 - iosPct).toFixed(0)}% Android`
              : '—'
          }
          Icon={Smartphone}
          detail={
            data
              ? `${fmtInt.format(data.platformMix.ios)} iOS · ${fmtInt.format(data.platformMix.android)} Android`
              : undefined
          }
        />
        <Kpi
          label="New devices 24h"
          display={data ? fmtInt.format(data.devicesNew24h) : '—'}
          Icon={UserPlus}
        />
        <Kpi
          label="Cold-start p95"
          display={
            data?.coldStartP95Ms !== null && data?.coldStartP95Ms !== undefined
              ? `${data.coldStartP95Ms.toFixed(0)} ms`
              : '—'
          }
          Icon={Timer}
          detail="PostHog wiring in a later slice"
        />
        <Kpi
          label="Crash-free sessions"
          display={
            data?.crashFreeSessionsPct !== null && data?.crashFreeSessionsPct !== undefined
              ? `${data.crashFreeSessionsPct.toFixed(2)}%`
              : '—'
          }
          Icon={ZapOff}
          detail="Sentry wiring in a later slice"
        />
      </div>

      {query.error && (
        <div style={{ color: 'var(--error)', fontSize: 'var(--fs-sm)' }}>
          Failed to load mobile overview: {(query.error as Error).message}
        </div>
      )}
    </>
  );
}
